<template>
  <div>
    <Head title="Respaldos del Sistema" />

    <div class="flex items-center justify-between mb-8">
      <h1 class="text-3xl font-bold text-gray-800">
        <i class="fa fa-database mr-2 text-teal-600"></i> Respaldos del Sistema
      </h1>
    </div>

    <div class="bg-white rounded-xl shadow-lg border border-teal-100 overflow-hidden mb-8">
      <div class="p-6 bg-teal-50 border-b border-teal-100">
        <h2 class="text-xl font-bold text-teal-800">Exportar Datos</h2>
        <p class="text-gray-600 mt-1">Genera y descarga un respaldo de la información de tu sistema.</p>
      </div>

      <div class="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        <!-- Respaldo Completo -->
        <div class="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow bg-gray-50 flex flex-col justify-between">
          <div>
            <div class="w-12 h-12 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center text-xl mb-4">
              <i class="fa fa-cubes"></i>
            </div>
            <h3 class="font-bold text-lg text-gray-800 mb-2">Respaldo Completo</h3>
            <p class="text-sm text-gray-600 mb-4">
              Descarga un archivo ZIP que incluye tanto la base de datos completa como todos los archivos multimedia (imágenes, documentos, etc).
            </p>
          </div>
          <a :href="baseUrl + '/backup/export?type=full'" target="_blank" class="btn-blue w-full flex justify-center items-center gap-2 py-3">
            <i class="fa fa-download"></i> Descargar Completo
          </a>
        </div>

        <!-- Solo Base de Datos -->
        <div class="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow bg-gray-50 flex flex-col justify-between">
          <div>
            <div class="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xl mb-4">
              <i class="fa fa-server"></i>
            </div>
            <h3 class="font-bold text-lg text-gray-800 mb-2">Sólo Base de Datos</h3>
            <p class="text-sm text-gray-600 mb-4">
              Descarga únicamente un respaldo SQL de los registros almacenados en el sistema sin los archivos pesados.
            </p>
          </div>
          <a :href="baseUrl + '/backup/export?type=database'" target="_blank" class="w-full flex justify-center items-center gap-2 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow">
            <i class="fa fa-download"></i> Descargar Datos
          </a>
        </div>

        <!-- Solo Archivos -->
        <div class="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow bg-gray-50 flex flex-col justify-between">
          <div>
            <div class="w-12 h-12 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xl mb-4">
              <i class="fa fa-images"></i>
            </div>
            <h3 class="font-bold text-lg text-gray-800 mb-2">Sólo Archivos</h3>
            <p class="text-sm text-gray-600 mb-4">
              Descarga un archivo conteniendo los elementos multimedia, logos y fotos cargados por cuentas y productos.
            </p>
          </div>
          <a :href="baseUrl + '/backup/export?type=storage'" target="_blank" class="w-full flex justify-center items-center gap-2 py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors shadow">
            <i class="fa fa-download"></i> Descargar Archivos
          </a>
        </div>
      </div>
    </div>

    <!-- Restaurar Respaldo -->
    <div class="bg-white rounded-xl shadow-lg border border-teal-100 overflow-hidden mb-8">
      <div class="p-6 bg-teal-50 border-b border-teal-100">
        <h2 class="text-xl font-bold text-teal-800">Restaurar Datos</h2>
        <p class="text-gray-600 mt-1">Sube un archivo .zip generado previamente por este sistema para restaurarlo.</p>
      </div>

      <div class="p-6">
        <form @submit.prevent="importBackup" class="flex flex-col md:flex-row gap-4 items-center">
          <input 
            type="file" 
            ref="backupInput"
            accept=".zip"
            @change="handleFileChange"
            class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
            required
          />
          <button 
            type="submit" 
            class="w-full md:w-auto flex justify-center items-center gap-2 py-3 px-6 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors shadow whitespace-nowrap disabled:opacity-50"
            :disabled="form.processing"
          >
            <i class="fa" :class="form.processing ? 'fa-spinner fa-spin' : 'fa-upload'"></i> 
            {{ form.processing ? 'Restaurando...' : 'Restaurar Respaldo' }}
          </button>
        </form>
        <p v-if="importMessage" :class="importSuccess ? 'text-green-600' : 'text-red-600'" class="mt-4 font-semibold">
          {{ importMessage }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from '@/Shared/Layout.vue'
import { Head, Link, useForm } from '@inertiajs/inertia-vue3'

export default {
  components: {
    Head,
    Link
  },
  layout: Layout,
  data() {
    return {
       baseUrl: window.location.origin,
       form: useForm({
         backup_file: null
       }),
       importMessage: '',
       importSuccess: false
    }
  },
  methods: {
    handleFileChange(e) {
      if(e.target.files.length) {
        this.form.backup_file = e.target.files[0];
      }
    },
    importBackup() {
      if(!this.form.backup_file) return;

      this.importMessage = '';

      this.form.post('/backup/import', {
        preserveScroll: true,
        forceFormData: true,
        onSuccess: (page) => {
          this.importMessage = page.props.flash?.success || 'Respaldo restaurado correctamente';
          this.importSuccess = true;
          this.$refs.backupInput.value = '';
          this.form.backup_file = null;
        },
        onError: (errors) => {
          this.importMessage = errors.backup_file || errors.message || 'Ocurrió un error al restaurar el respaldo.';
          this.importSuccess = false;
        }
      });
    }
  }
}
</script>
